import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
    ButtonPrimary,
    ButtonWorkspaceDropdown,
    ButtonBorderless,
    ButtonTertiary,
} from "@nova/component-btn"
import UploadDropbox from "@nova/component-upload-dropbox"
import { validateEmailAddress } from "@nova/util-strings"
import { useTranslation } from "@nova/use-translation"
import { useAsyncCallback } from "@nova/use-async-callback"
import { useInviteEverywhereModalState } from "./useInviteEverywhereModalState"
import { Permissions } from "@nova/constants"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspace } from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    selectAllWorkspaceMembers,
    setRequestModalId,
} from "features/entity-caching/members/UserManagementSlice"
import { InviteEverywhereCollaborator } from "./InviteEverywhereCollaborator"
import { ScrollableArea } from "@nova/component-scrollable-area"
import classNames from "classnames"
import { PermissionUpdates } from "./InviteEverywhereModalContainer"
import { sortAndFilterMembers } from "./module"
import { selectAllFires } from "features/entity-caching/fires/selectors"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { ModalNames } from "../types"
import { CopyInviteLinkButton } from "./CopyInviteLinkButton"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import * as Icons from "@nova/component-icons"
import { WorkspaceAccessSplitButton } from "features/data-management/workspace/workspace/body/settings-and-collaborators/collaborators/WorkspaceAccessSplitButton"
import { useContactLists } from "features/data-management/workspace/workspace/body/settings-and-collaborators/mission-groups/useContactListFunctions"
import { OrganizationExtended, OrganizationPermissions, WorkspaceMember } from "@nova/schemas"
import { useRouter } from "pages"
import { ContactInputMultiSelect } from "themes/default-tailwind/buttons/ContactInput"
import { DiscriminatedUnion } from "@nova/util-ts"

export const parseCSVFile: (csvFile: File | undefined) => Promise<string[]> = async (csvFile) => {
    if (!csvFile) throw new Error("CSV file is undefined")

    const content: string = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
            resolve(reader.result as string)
        }
        reader.onerror = () => {
            reject(reader.error)
        }
        reader.readAsText(csvFile)
    })

    const lines = content.split(/\r?\n/).map((line) => line.trim())

    const firstLine = lines[0]
    const isHeader = /^[a-zA-Z\s]+$/.test(firstLine)

    // Skip the first line if it's a header
    const dataLines = isHeader ? lines.slice(1) : lines

    // Parse remaining lines into emails
    const emails = dataLines
        .join(",")
        .split(",")
        .map((str) => str.trim())
        .filter((email) => email !== "")

    return emails
}

const getPrimaryButtonLabel = (mode: string, hasPermissionUpdates?: boolean) => {
    if (mode === "view") {
        if (hasPermissionUpdates) return "Confirm Changes"
        return "Done"
    }
    return "Send Invite"
}

type EntitySelectorItem = {
    id: string
    fullName: string
    icon: Icons.IconComponent
}

export type InviteEverywhereModalProps = {
    inviteDestinations: EntitySelectorItem[]
    allMembers: Record<string, WorkspaceMember[]>
    closeModal: () => void
    sendInvites: (
        org: OrganizationExtended,
        users: string[],
        permissions: OrganizationPermissions
    ) => Promise<void>
    updatePermissions: (
        members: DiscriminatedUnion<WorkspaceMember>[],
        permissionUpdates: PermissionUpdates
    ) => void
    getMembers: (organizationId: string) => Promise<void>
}
export const InviteEverywhereModal = ({
    inviteDestinations,
    allMembers,
    closeModal,
    sendInvites,
    updatePermissions,
}: InviteEverywhereModalProps) => {
    const router = useRouter("/")
    const {
        csvFile,
        setCsvFile,
        emails,
        setEmails,
        emailInput,
        setEmailInput,
        selectedContactLists,
        setSelectedContactLists,
    } = useInviteEverywhereModalState()

    const { contactLists } = useContactLists()

    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const activeWorkspace = useAppSelector(selectActiveWorkspace())
    const activeWorkspaceId = activeWorkspace?.id || ""
    const projects = useAppSelector(selectAllFires)
    const members = useAppSelector(selectAllWorkspaceMembers(activeWorkspaceId))

    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [dropdownSelection, setDropdownSelection] = useState(Permissions.Editor)
    const [mode, setMode] = useState<"view" | "invite">("view")
    const [inviteMethod, setInviteMethod] = useState<"Email" | "CSV">("Email")
    const [permissionUpdates, setPermissionUpdates] = useState<PermissionUpdates>({})

    const selectorItems = inviteDestinations
    const [inviteDestination, setInviteDestination] = useState(selectorItems[0])

    const { isEditor } = useWorkspacePermissions(inviteDestination?.id)

    const { directSorted, inheritedSorted } = useMemo(
        () =>
            sortAndFilterMembers(
                members || [],
                inviteDestination?.id || "",
                activeWorkspace,
                inviteDestination ? projects[inviteDestination?.id] : undefined
            ),
        [members, inviteDestination, activeWorkspace, projects]
    )
    //if a destination is selected that the user doesn't have permissions for, open access request modal
    useEffect(() => {
        if (!isEditor && inviteDestination !== null) {
            dispatch(initializeCenterModalForComponentAction(ModalNames.RequestAccessModal))
            dispatch(setRequestModalId(inviteDestination.id))
        }
    }, [isEditor, inviteDestination, dispatch])

    const { loading, error, collectUsers } = useAsyncCallback(
        {
            collectUsers: async () => {
                const workspaceId = activeWorkspace?.id
                if (!workspaceId) return
                const emailsToSend: string[] = [
                    ...emails,
                    ...selectedContactLists.flatMap((list) =>
                        list.contacts
                            .filter((contact) => contact.type === "email")
                            .map((contact) => contact.email)
                    ),
                ]

                if (validateEmailAddress(emailInput)) {
                    emailsToSend.push(emailInput)
                }

                const users =
                    inviteMethod === "CSV" ? await parseCSVFile(csvFile?.[0]) : emailsToSend
                // should not be able to be here without org set
                if (!inviteDestination) throw new Error("Could not send invites")
                // should only be possible to be here if csv file is empty
                if (users.length === 0 && !validateEmailAddress(emailInput))
                    throw new Error("Could not send invites")
                else if (users.some((user) => !validateEmailAddress(user)))
                    throw new Error("Invalid email format")

                const isFullInvite = inviteDestination.id === workspaceId
                const permissions = isFullInvite
                    ? { workspaceAccess: dropdownSelection, accessRequestIds: [], subRoles: [] }
                    : {
                          subRoles: [{ id: inviteDestination.id, permission: dropdownSelection }],
                          workspaceAccess: Permissions.None,
                          accessRequestIds: [],
                      }
                if (users.length !== 0) await sendInvites(activeWorkspace, users, permissions)
                closeModal()
            },
        },
        [
            inviteDestination,
            dropdownSelection,
            emails,
            selectedContactLists,
            inviteMethod,
            csvFile,
            allMembers,
            emailInput,
        ]
    )

    const contentRef = useRef<HTMLDivElement>(null)
    // Animate height on mode change
    const switchMode = (newMode: "view" | "invite") => {
        const contentElement = contentRef.current
        if (!contentElement) return

        const prevHeight = contentElement.scrollHeight
        contentElement.style.height = prevHeight + "px"
        // Force reflow to ensure the browser registers the height
        void contentElement.offsetHeight

        // Attach the transitionend event listener before changing height
        const handleTransitionEnd = () => {
            contentElement.style.height = "auto"
            contentElement.removeEventListener("transitionend", handleTransitionEnd)
        }
        contentElement.addEventListener("transitionend", handleTransitionEnd)

        setMode(newMode)

        // Allow the content to update before measuring the new height
        setTimeout(() => {
            const newHeight = contentElement.scrollHeight

            // If shrinking, and new height is smaller, set the new height
            if (newHeight < prevHeight) {
                contentElement.style.height = newHeight + "px"
                void contentElement.offsetHeight
            } else {
                // For expanding, set height to auto first
                contentElement.style.height = "auto"
                const expandedHeight = contentElement.scrollHeight

                contentElement.style.height = prevHeight + "px"
                void contentElement.offsetHeight
                contentElement.style.height = expandedHeight + "px"
            }
        }, 0)
    }

    //revert back to default view if the email input is cleared while inviting
    useEffect(() => {
        if (
            mode === "invite" &&
            inviteMethod !== "CSV" &&
            emailInput.length === 0 &&
            emails.length === 0 &&
            selectedContactLists.length === 0
        )
            switchMode("view")
    }, [mode, emails, inviteMethod, emailInput, selectedContactLists.length])

    const hasPermissionUpdates = Object.values(permissionUpdates).length > 0
    const handleModalCancel = useCallback(() => {
        closeModal()
    }, [closeModal])

    const csvName = csvFile?.[0].name || ""
    const primaryButtonLabel = getPrimaryButtonLabel(mode, hasPermissionUpdates)
    const hasAnyInvalidEmails = useMemo(() => {
        return emails.some((email) => !validateEmailAddress(email))
    }, [emails])
    const hasAnyPhoneNumbers = useMemo(() => {
        return selectedContactLists.some((list) =>
            list.contacts.some((contact) => contact.type === "phone")
        )
    }, [selectedContactLists])
    const inviteEntityType =
        inviteDestination.id === activeWorkspaceId
            ? "workspace"
            : !!projects[inviteDestination.id]
              ? "project"
              : "folder"

    const dropdownButtons = useMemo(
        () =>
            selectorItems.map((item) => {
                return {
                    label: item.fullName,
                    onClick: () => setInviteDestination(item),
                    icon: item.icon,
                }
            }),
        [selectorItems]
    )
    const readyToSendEmails =
        (validateEmailAddress(emailInput) || emails.length > 0 || contactLists.length > 0) &&
        !hasAnyInvalidEmails
    return (
        <>
            <HighPriorityNovaModalContainer
                title={
                    inviteMethod === "Email" ? `Share this ${inviteEntityType}` : "Upload CSV File"
                }
                closeModal={handleModalCancel}
                className="w-[30rem]"
                bottomBar={
                    <div className="w-full flex justify-end items-center gap-2">
                        <CopyInviteLinkButton entityId={inviteDestination.id} />
                        <div className="flex-grow" />
                        <ButtonTertiary title="Cancel" onClick={handleModalCancel} />
                        <ButtonPrimary
                            title={primaryButtonLabel}
                            RightIcon={Icons.ArrowRight}
                            onClick={() => {
                                if (mode === "invite") {
                                    collectUsers()
                                    return
                                }
                                if (
                                    Object.keys(permissionUpdates).length > 0 &&
                                    mode === "view" &&
                                    members
                                ) {
                                    updatePermissions(members, permissionUpdates)
                                }
                                closeModal()
                            }}
                            disabled={
                                mode !== "view" &&
                                (!inviteDestination ||
                                    (inviteMethod === "CSV"
                                        ? !csvFile?.[0]
                                        : !readyToSendEmails || loading))
                            }
                        />
                    </div>
                }
                onCancel={() => {
                    handleModalCancel()
                }}
                onReturn={
                    mode === "invite"
                        ? () => {
                              switchMode("view")
                              setEmails([])
                              setEmailInput("")
                              setInviteMethod("Email")
                          }
                        : undefined
                }
            >
                <div
                    ref={contentRef}
                    className={classNames("transition-height duration-300 ease-out text-gray-100")}
                    style={{ height: "auto" }}
                >
                    <h5 className="mt-4 mb-2">{t("Invite to")}</h5>
                    <div className="flex mb-5 gap-2">
                        <ButtonWorkspaceDropdown
                            widthClass="w-[16.563rem]"
                            label={inviteDestination.fullName}
                            labelIcon={inviteDestination.icon}
                            menuOpen={dropdownOpen}
                            setMenuOpen={setDropdownOpen}
                            dropdownButtons={dropdownButtons}
                        />
                        <WorkspaceAccessSplitButton
                            defaultPermission={Permissions.Editor}
                            dropdownSelection={dropdownSelection}
                            accessEntityId={inviteDestination?.id || activeWorkspaceId}
                            setDropdownSelection={setDropdownSelection}
                            allowRemoval={false}
                        />
                    </div>
                    {inviteMethod === "Email" && (
                        <>
                            <div className="flex justify-between items-center mb-2">
                                {mode === "view" && <h5> {t("Share with")}</h5>}
                                <h5
                                    onClick={() => {
                                        switchMode("invite")
                                        setInviteMethod("CSV")
                                    }}
                                    className={classNames(
                                        "cursor-pointer leading-[140%] underline decoration-solid",
                                        {
                                            hidden: mode === "invite",
                                        }
                                    )}
                                >
                                    {t("Import CSV")}
                                </h5>
                            </div>
                            <ContactInputMultiSelect
                                enabledFeatures={["email", "missionGroups"]}
                                warnForPhoneNumbers
                                emails={emails}
                                setEmails={(emails) => {
                                    setEmails(emails)
                                    if (mode !== "invite") {
                                        switchMode("invite")
                                    }
                                }}
                                missionGroups={selectedContactLists}
                                setMissionGroups={(missionGroups) => {
                                    setSelectedContactLists(missionGroups)
                                    if (mode !== "invite") {
                                        switchMode("invite")
                                    }
                                }}
                                placeholder="Add emails and Mission Groups, comma separated"
                            />
                        </>
                    )}
                    {inviteMethod === "CSV" && (
                        <div>
                            <UploadDropbox
                                allowedFileExtensions={["csv"]}
                                newItemsHandler={setCsvFile}
                                acceptMultiple={false}
                                mapExtensionsToMimeType={{ csv: "text/csv" }}
                                theme={"dark"}
                            />
                            {csvFile && (
                                <div className="text-left overflow-ellipsis overflow-hidden whitespace-nowrap ml-4">
                                    {csvName}
                                </div>
                            )}
                        </div>
                    )}
                    {mode === "view" && (
                        <div className="mt-6 relative">
                            <div className="flex justify-between items-center">
                                <div className="!text-left">{t("People with access")}</div>
                                <ButtonBorderless
                                    RightIcon={Icons.ArrowRight}
                                    title="Collaborator Settings"
                                    className="!p-0 !h-5"
                                    onClick={() => {
                                        router.navigate({
                                            path: "/workspace/:workspaceId/settings/users",
                                            params: { workspaceId: activeWorkspace?.id || "" },
                                        })
                                        closeModal()
                                    }}
                                />
                            </div>
                            <ScrollableArea className="max-h-[295px] flex flex-col gap-4 mt-2 p-6 py-4 border-[1px] rounded-md border-gray-700">
                                {directSorted.map((member) => {
                                    return (
                                        <InviteEverywhereCollaborator
                                            key={member.userId}
                                            member={member}
                                            entityId={inviteDestination?.id || activeWorkspaceId}
                                            permissionUpdates={permissionUpdates}
                                            setPermissionUpdates={setPermissionUpdates}
                                        />
                                    )
                                })}
                                {inheritedSorted.map((member, idx) => {
                                    return (
                                        <InviteEverywhereCollaborator
                                            key={idx}
                                            member={member}
                                            entityId={inviteDestination?.id || activeWorkspaceId}
                                            permissionUpdates={permissionUpdates}
                                            setPermissionUpdates={setPermissionUpdates}
                                            readOnly
                                        />
                                    )
                                })}
                            </ScrollableArea>
                        </div>
                    )}
                    <>
                        {hasAnyPhoneNumbers && (
                            <div className="mt-6 text-orange-500 flex">
                                <Icons.Icon Icon={Icons.Exclamation} />
                                <h5>{t("Phone numbers in Mission Groups will be excluded")}</h5>
                            </div>
                        )}
                        {(hasAnyInvalidEmails || (error as Error)) && (
                            <div
                                className={classNames("text-red-500 flex", {
                                    "mt-6": !hasAnyPhoneNumbers,
                                    "mt-1": hasAnyPhoneNumbers,
                                })}
                            >
                                <Icons.Icon Icon={Icons.Exclamation} />
                                <h5>
                                    {t(
                                        hasAnyInvalidEmails
                                            ? "Invalid email format"
                                            : (error as Error)?.message
                                    )}
                                </h5>
                            </div>
                        )}
                    </>
                </div>
            </HighPriorityNovaModalContainer>
            <div id="access-dropdown-portal" className="w-full" />
        </>
    )
}
